import { type ComponentPropsWithoutRef, type FC, type ReactNode, useRef } from 'react'

import * as DialogPrimitive from '@radix-ui/react-dialog'
import { styled } from 'styled-components'

import IconButton from 'core/components/lib/IconButton'
import { R } from 'core/helpers'
import { onSurface, outline, surface, surfaceBright, surfaceDim } from 'core/styles'

type SheetProps = {
  isOpen: boolean
  onClose: () => void
  children: ReactNode
  // Trigger is optional. Just a convenience prop to allow you to pass in a trigger element.
  trigger?: ReactNode
  title?: ReactNode
  actionButtons?: ReactNode
  preventClose?: boolean
  onOpenChange?: (open: boolean) => void
  onOpenAutoFocus?: ComponentPropsWithoutRef<typeof DialogPrimitive.Content>['onOpenAutoFocus']
}

const Sheet: FC<SheetProps> = ({
  isOpen,
  onClose,
  children,
  trigger,
  title,
  actionButtons,
  preventClose = false,
  onOpenChange,
  onOpenAutoFocus,
}) => {
  const titleRef = useRef<HTMLHeadingElement | null>(null)

  return (
    <DialogPrimitive.Root
      onOpenChange={(nextIsOpen) => {
        if (!nextIsOpen) onClose()
        onOpenChange?.(nextIsOpen)
      }}
      open={isOpen}
    >
      {!R.isNil(trigger) && <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger>}
      <DialogPrimitive.Portal>
        <Content
          onKeyDown={(event) => {
            // No need to close the sheet on escape key down
            // The user may have unfinished work and we don't want to lose it
            if (event.key === 'Escape') {
              event.preventDefault()
              event.stopPropagation()
            }
          }}
          onOpenAutoFocus={(event) => {
            if (onOpenAutoFocus) {
              onOpenAutoFocus(event)
              return
            }
            event.preventDefault()
            requestAnimationFrame(() => titleRef.current?.focus())
          }}
          onPointerDownOutside={(event) => {
            if (preventClose) event.preventDefault()
          }}
        >
          <CloseButtonWrapper>
            <DialogPrimitive.Close asChild>
              <IconButton ariaLabel='Close' name='close' onClick={onClose} />
            </DialogPrimitive.Close>
          </CloseButtonWrapper>
          <Main>
            <MainInner>
              {!!title && (
                <Header>
                  <TitleContainer>
                    <DialogPrimitive.Title asChild>
                      <Title ref={titleRef} tabIndex={-1}>
                        {title}
                      </Title>
                    </DialogPrimitive.Title>
                  </TitleContainer>
                </Header>
              )}
              <Body $hasActionButtons={!!actionButtons}>{children}</Body>
            </MainInner>
          </Main>
          {!!actionButtons && (
            <Bottom>
              <ActionButtons>{actionButtons}</ActionButtons>
            </Bottom>
          )}
        </Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

export default Sheet

const Content = styled(DialogPrimitive.Content)`
  display: flex;
  position: fixed;
  inset: 0;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  z-index: 10;
  outline: none;
  background: ${surface};
  width: 100%;
  height: 100%;
`

const CloseButtonWrapper = styled.div`
  display: flex;
  position: absolute;
  top: 40px;
  left: 40px;
  align-items: center;
  justify-content: center;
  z-index: 1;
  border-radius: 100%;
  background: ${surfaceDim};
  width: 40px;
  height: 40px;
`

const Main = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  width: 100%;
  overflow: auto;
`

const MainInner = styled.div`
  padding: 56px 20px 72px;
  width: 600px;
`

const Header = styled.div`
  display: flex;
  flex-shrink: 0;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
`

const TitleContainer = styled.div`
  display: flex;
  flex: 1;
  gap: 8px;
  align-items: center;
  min-width: 0;
`

const Title = styled.h2`
  margin: 0 0 32px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${onSurface};
  font-size: 22px;
  font-weight: 600;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: none;
  }
`

// No styles on the body. Just here for better readability.
const Body = styled.div<{ $hasActionButtons?: boolean }>`
  padding-bottom: ${({ $hasActionButtons }) => ($hasActionButtons ? '120px' : '0')};
`

const Bottom = styled.div`
  display: flex;
  position: absolute;
  bottom: 0;
  align-items: center;
  justify-content: center;
  border-top: 1px solid ${outline};
  background: ${surfaceBright};
  width: 100%;
  height: 68px;
`

const ActionButtons = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: flex-end;
  padding: 0 20px;
  width: 600px;
`
